import {
  Body,
  Box,
  Children,
  Heading,
  LocalizedString,
  Space,
  Stack,
  TextChildren,
  unsafeLocalizedString,
  useIsMobileLayout,
  useIsTouchScreen,
  useUniqueID,
  CloseIcon,
  paletteG4P as palette,
} from "@dbn/omnichannel-design-system-g4p";
import * as classes from "./HeaderCZA11Y.css";
import { Logo } from "./Logo";
import { Option } from "fp-ts/Option";
import { constFalse, constNull, pipe } from "fp-ts/function";
import { option } from "fp-ts";
import { ActionIconProps, fold } from "./headerActionIcon";
import { HeaderMobileCZ } from "./HeaderMobile";
import cx from "classnames";
import { LogoOpen } from "./LogoOpen";
import { CommonProps } from "./CommonProps";
import {
  DownIcon,
  OverflowMenu as OverflowMenuG4p,
  ProfileIcon,
  UpIcon,
} from "@g4p/react-styleguide-library";
import { useMemo, useState } from "react";
import { MenuDropdown } from "../MenuDropdown/MenuDropdown";
import { HeaderTouchScreenA11Y } from "./HeaderTouchScreenA11Y";
import { RuntimeLocaleKey, useFormatMessage } from "../../intl";
import "./header.css";
import { ContainerHeader } from "./grid/ContainerHeader";

type Props = CommonProps & {
  title: Option<React.ComponentProps<typeof Heading>["children"]>;
  subtitle?: React.ComponentProps<typeof Body>["children"];
  remainingTime?: number;
  onLogoClick: Option<() => unknown>;
  iconAction: Option<ActionIconProps>;
  dropdownHeading: Option<LocalizedString>;
  centerContent?: Children;
  headerLeftMarginPx?: number;
};

export function HeaderCZA11Y(props: Props) {
  const isMobileLayout = useIsMobileLayout();
  const isTouchScreen = useIsTouchScreen();
  const formatMessage = useFormatMessage();
  const headerId = useUniqueID();

  const renderCenterContent = () => {
    if (!props.centerContent) return <></>;
    return (
      <Box
        grow
        vAlignContent="center"
        hAlignContent="center"
        className={classes.centerContentArea}
      >
        {props.centerContent}
      </Box>
    );
  };

  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const settingsMenuVisible = pipe(
    props.iconAction,
    option.map(
      fold(
        _menuOptions => true,
        _onExit => false
      )
    )
  );

  const exitButtonVisible = pipe(
    props.iconAction,
    option.map(
      fold(
        _menuOptions => false,
        _onExit => true
      )
    ),
    option.getOrElse(constFalse)
  );

  const items = () => {
    let itemArray = [];
    if (option.isSome(props.dropdownHeading)) {
      itemArray.push({
        title: props.dropdownHeading.value,
        label: props.dropdownHeading.value,
        type: "group" as "group",
        key: 0,
      });
    }
    pipe(
      props.iconAction,
      option.map(
        fold(
          menuOptions =>
            menuOptions.map((option, index) =>
              itemArray.push({
                title: option.label.toUpperCase() as string,
                label: option.label.toUpperCase() as string,
                type: "item" as "item",
                key: index + 1,
                onClick: () => {
                  option.action();
                },
              })
            ),
          _onExit => ({})
        )
      )
    );
    return itemArray;
  };

  const closeButton = useMemo(() => {
    return (
      <Box
        className={classes.exit}
        aria-roledescription="button"
        role={"button"}
        aria-label={formatMessage("generic.close.alt" as RuntimeLocaleKey)}
        tabIndex={0}
      >
        <CloseIcon size="default" />
      </Box>
    );
  }, []);

  if (isMobileLayout) {
    return <HeaderMobileCZ {...props} />;
  } else if (isTouchScreen) {
    return <HeaderTouchScreenA11Y {...props} />;
  } else {
    return (
      <div
        tabIndex={-1}
        id={headerId}
        style={{
          backgroundColor: palette.white,
          borderBottom: "1px solid #cccccc",
          width: "100%",
        }}
      >
        <ContainerHeader
          leftMarginPx={props.headerLeftMarginPx}
          leftColor={palette.white}
          centerColor={palette.white}
          rightColor={palette.white}
          leftContent={
            <Box
              shrink
              hAlignContent="left"
              vAlignContent="center"
              aria-hidden={true}
              className={cx(classes.logoArea, {
                [classes.logoAreaClickable]: pipe(
                  props.onLogoClick,
                  option.isSome
                ),
              })}
              onClick={pipe(props.onLogoClick, option.toUndefined)}
            >
              <Logo height={24} />
              <Space units={4} />
              <LogoOpen height={24} />
            </Box>
          }
          centerContent={renderCenterContent()}
          rightContent={
            <Box shrink vAlignContent="center" className={classes.productArea}>
              {props.subtitle !== undefined && (
                <Box
                  style={{
                    border: "1px solid #262626",
                    borderRadius: "6px",
                    padding: "6px 10px 6px 10px",
                  }}
                >
                  <Body
                    size="medium"
                    weight="regular"
                    color={palette.dark}
                    ariaLabel={
                      props.remainingTime !== undefined
                        ? formatMessage("SessionTimeout.altMessage", {
                            minutes: Math.floor(props.remainingTime / 60),
                            seconds: props.remainingTime % 60,
                          })
                        : ("" as LocalizedString)
                    }
                  >
                    {unsafeLocalizedString(props.subtitle as string)}
                  </Body>
                </Box>
              )}

              {props.subtitle !== undefined && exitButtonVisible ? (
                <Space units={10} />
              ) : null}

              {exitButtonVisible && (
                <Box column hAlignContent="right">
                  {pipe(
                    props.title,
                    option.fold(constNull, title => (
                      <Heading
                        size="small"
                        weight="regular"
                        color={palette.dark}
                      >
                        {title}
                      </Heading>
                    ))
                  )}
                </Box>
              )}

              {exitButtonVisible &&
                pipe(
                  props.iconAction,
                  option.map(
                    fold(
                      menuOptions => (
                        <MenuDropdown
                          heading={props.dropdownHeading}
                          options={menuOptions}
                        />
                      ),
                      onExit => (
                        <div
                          onClick={onExit}
                          aria-hidden={"true"}
                          tabIndex={-1}
                          style={{ marginRight: "2px", marginLeft: "2px" }}
                          onKeyDown={e => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onExit();
                            }
                          }}
                        >
                          {closeButton}
                        </div>
                      )
                    )
                  ),
                  option.map(actionIcon => <>{actionIcon}</>),
                  option.toNullable
                )}

              {(option.isSome(settingsMenuVisible) &&
                settingsMenuVisible.value) === true && (
                <Box
                  style={{ paddingLeft: "40px", paddingRight: "10px" }}
                  role="menu"
                  aria-label={formatMessage("generic.menu.ariaLabel")}
                >
                  <OverflowMenuG4p
                    menu={{ items: items() }}
                    arrow={true}
                    onOpenChange={setSettingsMenuOpen}
                  >
                    {props.title && (
                      <Stack
                        style={{
                          fontSize: "14px",
                          lineHeight: "24px",
                          textTransform: "uppercase",
                          alignItems: "end",
                        }}
                        units={2}
                        column
                        vAlignContent="center"
                        hAlignContent="right"
                      >
                        <ProfileIcon
                          width={"24px"}
                          height={"24px"}
                          aria-hidden={true}
                        />

                        <Box style={{ alignItems: "end", marginTop: "-8px" }}>
                          {option.isSome(props.title) ? (
                            <Body
                              size={"x-small"}
                              weight={"bold"}
                              color={
                                settingsMenuOpen
                                  ? palette.blue800
                                  : palette.dark
                              }
                            >
                              {
                                unsafeLocalizedString(
                                  props.title.value as string
                                ) as TextChildren
                              }
                            </Body>
                          ) : (
                            <></>
                          )}

                          {settingsMenuOpen ? (
                            <UpIcon
                              width={"16px"}
                              height={"16px"}
                              color={palette.blue800}
                              aria-hidden={true}
                            />
                          ) : (
                            <DownIcon
                              width={"16px"}
                              height={"16px"}
                              color={palette.dark}
                              aria-hidden={true}
                            />
                          )}
                        </Box>
                      </Stack>
                    )}
                  </OverflowMenuG4p>
                </Box>
              )}
            </Box>
          }
        />
      </div>
    );
  }
}
